import throttle from 'lodash/throttle';
import { useCallback, useRef } from 'react';
import type { MouseEventHandler, MouseEvent as ReactMouseEvent } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import { predictLeaveProbability } from './predictLeaveProbability.ts';
import type { MouseTrace } from './mouseTraceTypes.ts';

const sendAnalyticsEvent = (
	createAnalyticsEvent: ReturnType<typeof useAnalyticsEvents>['createAnalyticsEvent'],
	action: string,
	itemId: string,
	attributes: Record<string, any>,
) => {
	createAnalyticsEvent({
		type: 'sendTrackEvent',
		data: {
			action,
			actionSubject: 'pageTree',
			actionSubjectId: itemId,
			source: 'pageTree',
			attributes,
		},
	}).fire();
};

const toMouseTraceString = (mouseTrace: MouseTrace) => {
	return mouseTrace.entries
		.map(({ x, y, t }) => `(${Math.round(x)}|${Math.round(y)}|${Math.round(t)})`)
		.join('-');
};

// For now this will only collect user's mouse movements compared to the initial target element
// and log them in the following format: (x|y|t)-(x|y|t)-... so later we can TRY using this data
// to determine if the user is intended to click on the page tree item.
const isUserIntendedToClick = throttle(
	(
		event: ReactMouseEvent,
		targetRect: DOMRect | undefined,
		mouseTrace: MouseTrace,
		preloadTimeout: number,
	) => {
		if (!targetRect) {
			return true;
		}

		const x = event.clientX - targetRect.left;
		const y = event.clientY - targetRect.top;
		const t = performance.now() - mouseTrace.timestamp;

		mouseTrace.entries.push({ x, y, t });

		// We don't want to store too much data in the mouse trace, so we limit it to 200 entries,
		// the number of entries is arbitrary, but hopefully should be enough to determine if the
		// user is intended to click on the page tree item.
		if (mouseTrace.entries.length > 200) {
			mouseTrace.entries.shift();
		}

		// Evaluate the probability of the user leaving the element after the preload timeout
		if (mouseTrace.leaveProbability === undefined && t > preloadTimeout) {
			const result = predictLeaveProbability(mouseTrace.entries);
			mouseTrace.leaveProbability = result.leaveProbability;
			mouseTrace.modelVersion = result.version;
		}

		return true;
	},
	17,
);

/**
 * This hook is used to (potentially) preload the JS and data for the page tree item when the user
 * hovers over the page tree item for a certain amount of time.
 *
 * First stage is an experiment aimed to gather insights on the user behavior.
 */
export const usePreloadOnDelayedHover = ({
	itemId,
	isLive,
	contentType,
	isSelected,
}: {
	itemId: string;
	isLive: boolean;
	contentType: string;
	isSelected: boolean;
}): {
	isPreloadOnDelayedHoverEnabled: boolean;
	onPreloadMouseEnter: MouseEventHandler<HTMLElement>;
	onPreloadMouseLeave: () => void;
	onPreloadMouseDown: () => void;
	onPreloadMouseMove: MouseEventHandler<HTMLElement>;
} => {
	const preloadTimeout = StatsigConfigurations.getNumber('confluence-delayed-preload-timeout', 500);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const preloadAndLeaveRef = useRef<boolean>(false);
	const interactionTimeRef = useRef<number>(0);
	const hoverTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
	const mouseTraceRef = useRef<MouseTrace>({ entries: [], timestamp: performance.now() });
	const targetRectRef = useRef<DOMRect | undefined>(undefined);

	const onPreloadMouseEnter = useCallback<MouseEventHandler<HTMLElement>>(
		(event) => {
			if (isSelected || !fg('confluence-delayed-preload-hover')) {
				return;
			}

			targetRectRef.current = (event.target as HTMLElement).getBoundingClientRect();
			mouseTraceRef.current = { entries: [], timestamp: performance.now() };
			preloadAndLeaveRef.current = false;
			interactionTimeRef.current = performance.now();

			hoverTimeoutRef.current = setTimeout(() => {
				preloadAndLeaveRef.current = true;
				// NO-OP: with this we just measure user behavior, not actually preload the page yet
			}, preloadTimeout);
		},
		// NOTE: dependency list is intentional, as future preloading function will depend on these values
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[itemId, isLive, contentType, isSelected],
	);

	const onPreloadMouseLeave = useCallback((): void => {
		if (isSelected || !fg('confluence-delayed-preload-hover')) {
			return;
		}

		const mouseTrace = toMouseTraceString(mouseTraceRef.current);
		const modelVersion = mouseTraceRef.current.modelVersion;
		const leaveProbability = mouseTraceRef.current.leaveProbability;

		mouseTraceRef.current = { entries: [], timestamp: performance.now() };
		targetRectRef.current = undefined;

		if (hoverTimeoutRef.current) {
			clearTimeout(hoverTimeoutRef.current);
			hoverTimeoutRef.current = undefined;
			if (preloadAndLeaveRef.current) {
				preloadAndLeaveRef.current = false;

				// Here we want to know how many times the user has hovered over the page tree item
				// enough to start the preload, but then left without the navigation.
				sendAnalyticsEvent(createAnalyticsEvent, 'preloadAndLeave', itemId, {
					isLive,
					contentType,
					isNav4Enabled: true,
					preloadTimeout,
					leaveAfter: performance.now() - interactionTimeRef.current,
					mouseTrace,
					leaveProbability,
					modelVersion,
				});
			}
		}
	}, [itemId, createAnalyticsEvent, isLive, contentType, preloadTimeout, isSelected]);

	const onPreloadMouseDown = useCallback((): void => {
		if (isSelected || !fg('confluence-delayed-preload-hover')) {
			return;
		}

		const mouseTrace = toMouseTraceString(mouseTraceRef.current);
		const { leaveProbability, version } = predictLeaveProbability(mouseTraceRef.current.entries);

		clearTimeout(hoverTimeoutRef.current);
		mouseTraceRef.current = { entries: [], timestamp: performance.now() };
		hoverTimeoutRef.current = undefined;
		preloadAndLeaveRef.current = false;

		sendAnalyticsEvent(createAnalyticsEvent, 'preloadAndClick', itemId, {
			isLive,
			contentType,
			isNav4Enabled: true,
			preloadTimeout,
			clickAfter: performance.now() - interactionTimeRef.current,
			mouseTrace,
			leaveProbability,
			modelVersion: version,
		});
	}, [createAnalyticsEvent, itemId, isLive, contentType, preloadTimeout, isSelected]);

	const onPreloadMouseMove = useCallback<MouseEventHandler<HTMLElement>>(
		(event) => {
			if (isSelected || !fg('confluence-delayed-preload-hover')) {
				return;
			}

			// If we determine that the user is not intended to click, we don't want to preload the page
			// so cancel the preloading timeout and reset the rest of the state.
			if (
				!isUserIntendedToClick(event, targetRectRef.current, mouseTraceRef.current, preloadTimeout)
			) {
				clearTimeout(hoverTimeoutRef.current);
				hoverTimeoutRef.current = undefined;
				preloadAndLeaveRef.current = false;
			}
		},
		[isSelected, preloadTimeout],
	);

	return {
		isPreloadOnDelayedHoverEnabled: fg('confluence-delayed-preload-hover'),
		onPreloadMouseEnter,
		onPreloadMouseLeave,
		onPreloadMouseDown,
		onPreloadMouseMove,
	};
};
